/**
 * SchedulingView — Smart multi-participant scheduling with availability overlay
 *
 * Connects to /api/scheduling/* endpoints (scheduling-routes.ts).
 * Sections: Stats bar -> Session list -> Session detail (overlay + suggestions)
 */

import { useState, useEffect, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SchedulingSession {
  _key?: string;
  title: string;
  organizerId: string;
  participants: Participant[];
  durationMinutes: number;
  horizon: { start: string; end: string };
  preferences: SchedulingPreferences;
  status: 'draft' | 'computing' | 'ready' | 'booked' | 'cancelled';
  overlay?: AvailabilityOverlay;
  selectedSlot?: Suggestion;
  createdAt?: string;
  updatedAt?: string;
}

interface Participant {
  contactId: string;
  contactName: string;
  contactEmail?: string;
  required: boolean;
}

interface SchedulingPreferences {
  preferredTimeOfDay?: 'morning' | 'afternoon' | 'evening' | 'any';
  preferredDays?: string[];
  bufferBefore?: number;
  bufferAfter?: number;
  workingHoursOnly?: boolean;
  timezone?: string;
}

interface AvailabilityOverlay {
  sessionKey: string;
  suggestions: Suggestion[];
  days: OverlayDay[];
  stats: OverlayStats;
  computedAt: string;
}

interface OverlayDay {
  date: string;
  dayOfWeek: string;
  suggestions: Suggestion[];
  hasViableSlots: boolean;
  organizerEvents: OrganizerEvent[];
  participantBlocks: ParticipantDayBlocks[];
}

interface OrganizerEvent {
  summary: string;
  start: string;
  end: string;
  status: string;
}

interface ParticipantDayBlocks {
  contactId: string;
  contactName: string;
  blocks: Array<{ start: string; end: string; status: string }>;
  confidence: number;
}

interface Suggestion {
  id: string;
  start: string;
  end: string;
  durationMinutes: number;
  score: number;
  availableParticipants: string[];
  conflicts: ConflictAnnotation[];
  annotations: SlotAnnotation[];
}

interface ConflictAnnotation {
  contactId: string;
  contactName: string;
  type: string;
  severity: string;
  description: string;
}

interface SlotAnnotation {
  type: string;
  message: string;
}

interface OverlayStats {
  totalSlots: number;
  viableSlots: number;
  perfectSlots: number;
  daysWithSlots: number;
  totalDays: number;
  computeTimeMs: number;
}

interface SchedulingStats {
  totalSessions: number;
  activeSessions: number;
  bookedSessions: number;
}

interface FreeBusySource {
  _key?: string;
  contactId: string;
  contactName: string;
  contactEmail?: string;
  type: string;
  status: 'active' | 'permission-denied' | 'unavailable' | 'untested';
  lastFetchedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ── API helpers ───────────────────────────────────────────────────────────────

const BASE = '/api/scheduling';

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} ${formatTime(start)} – ${formatTime(end)}`;
}

function scoreLabel(score: number): string {
  if (score >= 0.8) return 'Excellent';
  if (score >= 0.6) return 'Good';
  if (score >= 0.4) return 'Fair';
  return 'Poor';
}

function scoreClass(score: number): string {
  if (score >= 0.8) return 'amp-sched-score--high';
  if (score >= 0.6) return 'amp-sched-score--good';
  if (score >= 0.4) return 'amp-sched-score--fair';
  return 'amp-sched-score--low';
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="amp-sched-badge" data-status={status}>
      {status}
    </span>
  );
}

// ── Create Session Dialog ─────────────────────────────────────────────────────

function CreateSessionForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(30);
  const [participants, setParticipants] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const parts = participants
        .split('\n')
        .filter((l) => l.trim())
        .map((line) => {
          const [name, email] = line.split(',').map((s) => s.trim());
          return {
            contactId: `contact_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            contactName: name || 'Unknown',
            contactEmail: email || undefined,
            required: true,
          };
        });
      await apiFetch('/sessions', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          organizerId: 'daniel',
          participants: parts,
          durationMinutes: duration,
        }),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="amp-sched-form">
      <h3>New Scheduling Session</h3>
      {error && <div className="amp-sched-error">{error}</div>}
      <label className="amp-sched-label">
        Title
        <input
          className="amp-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Q1 Planning with Claire + Mark"
        />
      </label>
      <label className="amp-sched-label">
        Duration (minutes)
        <select
          className="amp-input"
          value={duration}
          onChange={(e) => setDuration(+e.target.value)}
        >
          <option value={15}>15 min</option>
          <option value={30}>30 min</option>
          <option value={45}>45 min</option>
          <option value={60}>1 hour</option>
          <option value={90}>1.5 hours</option>
          <option value={120}>2 hours</option>
        </select>
      </label>
      <label className="amp-sched-label">
        Participants (one per line: Name, email)
        <textarea
          className="amp-input amp-sched-textarea"
          rows={4}
          value={participants}
          onChange={(e) => setParticipants(e.target.value)}
          placeholder={'Claire Yang, claire@example.com\nMark Smith, mark@example.com'}
        />
      </label>
      <div className="amp-sched-form-actions">
        <button className="amp-btn amp-btn--primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creating...' : 'Create Session'}
        </button>
        <button className="amp-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Session Card ──────────────────────────────────────────────────────────────

function SessionCard({
  session,
  selected,
  onClick,
}: {
  session: SchedulingSession;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={`amp-sched-card ${selected ? 'amp-sched-card--selected' : ''}`}
      onClick={onClick}
    >
      <div className="amp-sched-card-header">
        <span className="amp-sched-card-title">{session.title}</span>
        <StatusBadge status={session.status} />
      </div>
      <div className="amp-sched-card-meta">
        <span>{session.durationMinutes} min</span>
        <span>{session.participants.length} participants</span>
        {session.updatedAt && <span>{formatDate(session.updatedAt)}</span>}
      </div>
      {session.selectedSlot && (
        <div className="amp-sched-card-booked">
          Booked: {formatDateRange(session.selectedSlot.start, session.selectedSlot.end)}
        </div>
      )}
    </div>
  );
}

// ── Overlay Day View ──────────────────────────────────────────────────────────

function DayView({ day }: { day: OverlayDay }) {
  return (
    <div className={`amp-sched-day ${day.hasViableSlots ? '' : 'amp-sched-day--empty'}`}>
      <div className="amp-sched-day-header">
        <span className="amp-sched-day-name">{day.dayOfWeek}</span>
        <span className="amp-sched-day-date">{formatDate(day.date)}</span>
        {day.suggestions.length > 0 && (
          <span className="amp-sched-day-count">{day.suggestions.length} slots</span>
        )}
      </div>
      {day.organizerEvents.length > 0 && (
        <div className="amp-sched-day-events">
          {day.organizerEvents.map((ev, i) => (
            <div key={i} className="amp-sched-event">
              <span className="amp-sched-event-time">{formatTime(ev.start)}</span>
              <span className="amp-sched-event-title">{ev.summary}</span>
            </div>
          ))}
        </div>
      )}
      {day.participantBlocks.length > 0 && (
        <div className="amp-sched-day-blocks">
          {day.participantBlocks.map((pb, i) => (
            <div key={i} className="amp-sched-participant-row">
              <span className="amp-sched-participant-name">{pb.contactName}</span>
              <div className="amp-sched-blocks">
                {pb.blocks.map((b, j) => (
                  <span key={j} className={`amp-sched-block amp-sched-block--${b.status}`}>
                    {formatTime(b.start)}–{formatTime(b.end)}
                  </span>
                ))}
                {pb.blocks.length === 0 && (
                  <span className="amp-sched-block amp-sched-block--free">Free</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Suggestion Card ───────────────────────────────────────────────────────────

function SuggestionCard({
  suggestion,
  onSelect,
  selecting,
}: {
  suggestion: Suggestion;
  onSelect: (id: string) => void;
  selecting: boolean;
}) {
  return (
    <div className="amp-sched-suggestion">
      <div className="amp-sched-suggestion-time">
        {formatDateRange(suggestion.start, suggestion.end)}
      </div>
      <div className="amp-sched-suggestion-meta">
        <span className={`amp-sched-score ${scoreClass(suggestion.score)}`}>
          {Math.round(suggestion.score * 100)}% {scoreLabel(suggestion.score)}
        </span>
        <span>{suggestion.availableParticipants.length} available</span>
        {suggestion.conflicts.length > 0 && (
          <span className="amp-sched-conflicts">
            {suggestion.conflicts.length} conflict{suggestion.conflicts.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {suggestion.conflicts.length > 0 && (
        <div className="amp-sched-conflict-list">
          {suggestion.conflicts.map((c, i) => (
            <div key={i} className="amp-sched-conflict-item">
              {c.contactName}: {c.description}
            </div>
          ))}
        </div>
      )}
      {suggestion.annotations.length > 0 && (
        <div className="amp-sched-annotations">
          {suggestion.annotations.map((a, i) => (
            <span key={i} className="amp-sched-annotation">
              {a.message}
            </span>
          ))}
        </div>
      )}
      <button
        className="amp-btn amp-btn--primary amp-btn--sm"
        onClick={() => onSelect(suggestion.id)}
        disabled={selecting}
      >
        Select Slot
      </button>
    </div>
  );
}

// ── Session Detail ────────────────────────────────────────────────────────────

function SessionDetail({
  session,
  onCompute,
  onSelect,
  onDelete,
  onBack,
}: {
  session: SchedulingSession;
  onCompute: () => void;
  onSelect: (suggestionId: string) => void;
  onDelete: () => void;
  onBack: () => void;
}) {
  const [computing, setComputing] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const handleCompute = async () => {
    setComputing(true);
    try {
      await onCompute();
    } finally {
      setComputing(false);
    }
  };

  const handleSelect = async (id: string) => {
    setSelecting(true);
    try {
      await onSelect(id);
    } finally {
      setSelecting(false);
    }
  };

  const overlay = session.overlay;

  return (
    <div className="amp-sched-detail">
      <div className="amp-sched-detail-header">
        <button className="amp-btn amp-btn--ghost" onClick={onBack}>
          &larr; Back
        </button>
        <h3>{session.title}</h3>
        <StatusBadge status={session.status} />
      </div>

      {/* Session info */}
      <div className="amp-sched-detail-info">
        <span>{session.durationMinutes} min</span>
        <span>
          {formatDate(session.horizon.start)} &ndash; {formatDate(session.horizon.end)}
        </span>
        {session.preferences.preferredTimeOfDay && (
          <span>Prefers: {session.preferences.preferredTimeOfDay}</span>
        )}
      </div>

      {/* Participants */}
      <div className="amp-sched-participants">
        <h4>Participants ({session.participants.length})</h4>
        {session.participants.map((p) => (
          <div key={p.contactId} className="amp-sched-participant">
            <span className="amp-sched-participant-name">{p.contactName}</span>
            {p.contactEmail && (
              <span className="amp-sched-participant-email">{p.contactEmail}</span>
            )}
            <span className={`amp-sched-badge ${p.required ? '' : 'amp-sched-badge--optional'}`}>
              {p.required ? 'Required' : 'Optional'}
            </span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="amp-sched-actions">
        {session.status === 'draft' && (
          <button className="amp-btn amp-btn--primary" onClick={handleCompute} disabled={computing}>
            {computing ? 'Computing...' : 'Compute Availability'}
          </button>
        )}
        {session.status === 'ready' && !session.selectedSlot && (
          <button className="amp-btn amp-btn--primary" onClick={handleCompute} disabled={computing}>
            {computing ? 'Recomputing...' : 'Recompute'}
          </button>
        )}
        {session.status !== 'booked' && (
          <button className="amp-btn amp-btn--danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>

      {/* Booked slot */}
      {session.selectedSlot && (
        <div className="amp-sched-booked">
          <h4>Booked</h4>
          <div className="amp-sched-booked-slot">
            {formatDateRange(session.selectedSlot.start, session.selectedSlot.end)}
            <span className={`amp-sched-score ${scoreClass(session.selectedSlot.score)}`}>
              {Math.round(session.selectedSlot.score * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Overlay */}
      {overlay && (
        <>
          {/* Stats */}
          <div className="amp-sched-overlay-stats">
            <span>
              {overlay.stats.viableSlots} viable / {overlay.stats.totalSlots} total slots
            </span>
            <span>{overlay.stats.perfectSlots} perfect</span>
            <span>
              {overlay.stats.daysWithSlots} / {overlay.stats.totalDays} days with options
            </span>
            <span>Computed in {overlay.stats.computeTimeMs}ms</span>
          </div>

          {/* Top suggestions */}
          {overlay.suggestions.length > 0 && session.status !== 'booked' && (
            <div className="amp-sched-suggestions">
              <h4>Top Suggestions</h4>
              {overlay.suggestions.slice(0, 5).map((s) => (
                <SuggestionCard
                  key={s.id}
                  suggestion={s}
                  onSelect={handleSelect}
                  selecting={selecting}
                />
              ))}
            </div>
          )}

          {/* Day-by-day overlay */}
          <div className="amp-sched-overlay">
            <h4>Calendar Overlay</h4>
            <div className="amp-sched-days">
              {overlay.days.map((day) => (
                <DayView key={day.date} day={day} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Sources Panel ─────────────────────────────────────────────────────────────

function SourcesPanel({ sources }: { sources: FreeBusySource[] }) {
  if (sources.length === 0) {
    return (
      <div className="amp-sched-sources-empty">
        No calendar sources connected. Sources provide live free/busy data for participants. Connect
        sources via Preampr settings.
      </div>
    );
  }
  return (
    <div className="amp-sched-sources">
      <h4>Calendar Sources ({sources.length})</h4>
      {sources.map((s) => (
        <div key={s._key} className="amp-sched-source">
          <span className={`amp-sched-source-dot amp-sched-source-dot--${s.status}`} />
          <span className="amp-sched-source-name">{s.contactName}</span>
          <span className="amp-sched-source-type">{s.type}</span>
          <StatusBadge status={s.status} />
        </div>
      ))}
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export function SchedulingView() {
  const [sessions, setSessions] = useState<SchedulingSession[]>([]);
  const [sources, setSources] = useState<FreeBusySource[]>([]);
  const [stats, setStats] = useState<SchedulingStats>({
    totalSessions: 0,
    activeSessions: 0,
    bookedSessions: 0,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<SchedulingSession | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [sessRes, srcRes, statsRes] = await Promise.all([
        apiFetch<{ data: SchedulingSession[] }>('/sessions'),
        apiFetch<{ data: FreeBusySource[] }>('/sources'),
        apiFetch<SchedulingStats>('/stats'),
      ]);
      setSessions(sessRes.data || []);
      setSources(srcRes.data || []);
      setStats(statsRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Load session detail when selected
  useEffect(() => {
    if (!selectedId) {
      setSelectedSession(null);
      return;
    }
    apiFetch<SchedulingSession>(`/sessions/${selectedId}`)
      .then(setSelectedSession)
      .catch(() => setSelectedSession(null));
  }, [selectedId]);

  const handleCompute = async () => {
    if (!selectedId) return;
    const overlay = await apiFetch<AvailabilityOverlay>(`/sessions/${selectedId}/compute`, {
      method: 'POST',
    });
    // Refresh session to get updated status
    const updated = await apiFetch<SchedulingSession>(`/sessions/${selectedId}`);
    setSelectedSession({ ...updated, overlay });
    loadAll();
  };

  const handleSelect = async (suggestionId: string) => {
    if (!selectedId) return;
    await apiFetch(`/sessions/${selectedId}/select`, {
      method: 'POST',
      body: JSON.stringify({ suggestionId }),
    });
    const updated = await apiFetch<SchedulingSession>(`/sessions/${selectedId}`);
    setSelectedSession(updated);
    loadAll();
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    await apiFetch(`/sessions/${selectedId}`, { method: 'DELETE' });
    setSelectedId(null);
    loadAll();
  };

  if (loading) {
    return (
      <div className="amp-sched-wrap">
        <div className="amp-sched-loading">Loading scheduling data...</div>
      </div>
    );
  }

  return (
    <div className="amp-sched-wrap">
      {error && <div className="amp-sched-error">{error}</div>}

      {/* Stats bar */}
      <div className="amp-sched-statsbar">
        <div className="amp-sched-stat">
          <span className="amp-sched-stat-value">{stats.totalSessions}</span>
          <span className="amp-sched-stat-label">Total</span>
        </div>
        <div className="amp-sched-stat">
          <span className="amp-sched-stat-value">{stats.activeSessions}</span>
          <span className="amp-sched-stat-label">Active</span>
        </div>
        <div className="amp-sched-stat">
          <span className="amp-sched-stat-value">{stats.bookedSessions}</span>
          <span className="amp-sched-stat-label">Booked</span>
        </div>
      </div>

      {/* Header */}
      <div className="amp-sched-header">
        <h2>Smart Scheduling</h2>
        {!showCreate && !selectedSession && (
          <button className="amp-btn amp-btn--primary" onClick={() => setShowCreate(true)}>
            + New Session
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreate && (
        <CreateSessionForm
          onCreated={() => {
            setShowCreate(false);
            loadAll();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Session detail OR session list */}
      {selectedSession ? (
        <SessionDetail
          session={selectedSession}
          onCompute={handleCompute}
          onSelect={handleSelect}
          onDelete={handleDelete}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        !showCreate && (
          <>
            {sessions.length === 0 ? (
              <div className="amp-sched-empty">
                No scheduling sessions yet. Create one to find the perfect meeting time with
                conflict-annotated availability overlays.
              </div>
            ) : (
              <div className="amp-sched-list">
                {sessions.map((s) => (
                  <SessionCard
                    key={s._key}
                    session={s}
                    selected={selectedId === s._key}
                    onClick={() => setSelectedId(s._key || '')}
                  />
                ))}
              </div>
            )}

            {/* Sources panel */}
            <SourcesPanel sources={sources} />
          </>
        )
      )}
    </div>
  );
}

export default SchedulingView;
